// React port of the legacy ReportFormComponent.vue: a per-report subset of
// date ranges (with shortcuts), remote selects, selects, text filters and
// tri-state (All / Yes / No) radios, with Submit / Reset.

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  ListItemText,
  Menu,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField
} from '@mui/material';
import { Calendar } from 'iconsax-reactjs';

import { adminApi } from '../../api/client';
import { t } from '../../i18n';
import { DatePresetKey, datePresets, presetRange, toInputValue, valueLabel } from './reportUtils';

export type FilterFieldName =
  | 'date'
  | 'created_at'
  | 'reference'
  | 'status'
  | 'type'
  | 'custom_fields'
  | 'location_id'
  | 'from_location'
  | 'to_location'
  | 'account_id'
  | 'category_id'
  | 'customer_id'
  | 'supplier_id'
  | 'user_id'
  | 'item_id'
  | 'serial'
  | 'title'
  | 'details'
  | 'approved'
  | 'draft'
  | 'paid'
  | 'void'
  | 'pos'
  | 'received';

/** Form values. Ranges are stored as `<field>_start` / `<field>_end` datetime-local strings. */
export type FilterValues = Record<string, string>;

type Option = { value: string; label: string };

const RANGE_FIELDS: FilterFieldName[] = ['date', 'created_at'];
const TRI_STATE_FIELDS: FilterFieldName[] = ['approved', 'draft', 'paid', 'void', 'pos', 'received'];
const TEXT_FIELDS: FilterFieldName[] = ['custom_fields', 'serial', 'title', 'details'];

// Remote selects (legacy *search endpoints, `?q=` typed search).
const REMOTE_FIELDS: Partial<Record<FilterFieldName, { endpoint: string; label: string }>> = {
  reference: { endpoint: 'references/search', label: 'report.filter.reference' },
  category_id: { endpoint: 'categories/search', label: 'report.filter.category' },
  customer_id: { endpoint: 'customers/search', label: 'report.filter.customer' },
  supplier_id: { endpoint: 'suppliers/search', label: 'report.filter.supplier' },
  user_id: { endpoint: 'users/search', label: 'report.filter.createdBy' },
  item_id: { endpoint: 'items/filter', label: 'report.filter.item' }
};

// Static selects loaded once (accounts/search, locations/search).
const STATIC_FIELDS: Partial<Record<FilterFieldName, { endpoint: string; label: string; key: string }>> = {
  account_id: { endpoint: 'accounts/search', label: 'report.filter.account', key: 'account_id' },
  location_id: { endpoint: 'locations/search', label: 'report.filter.location', key: 'location_id' },
  from_location: { endpoint: 'locations/search', label: 'report.filter.fromLocation', key: 'from_location' },
  to_location: { endpoint: 'locations/search', label: 'report.filter.toLocation', key: 'to_location' }
};

function toOptions(data: unknown): Option[] {
  if (!Array.isArray(data)) return [];
  return data
    .filter((row) => row && typeof row === 'object')
    .map((row) => {
      const r = row as Record<string, unknown>;
      return { value: String(r.value ?? r.id ?? ''), label: String(r.label ?? r.name ?? r.value ?? '') };
    })
    .filter((o) => o.value);
}

const staticCache = new Map<string, Promise<Option[]>>();
function loadStatic(endpoint: string) {
  if (!staticCache.has(endpoint)) {
    staticCache.set(
      endpoint,
      adminApi
        .get(endpoint)
        .then((r) => toOptions(r.data))
        .catch(() => {
          staticCache.delete(endpoint);
          return [];
        })
    );
  }
  return staticCache.get(endpoint)!;
}

function RemoteSelect({
  label,
  endpoint,
  value,
  initialLabel,
  onChange
}: {
  label: string;
  endpoint: string;
  value: string;
  initialLabel?: string;
  onChange: (value: string, option: Option | null) => void;
}) {
  const [options, setOptions] = useState<Option[]>([]);
  const [selected, setSelected] = useState<Option | null>(value ? { value, label: initialLabel ?? value } : null);
  const [loading, setLoading] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keep the selected option in sync when the parent resets / pre-fills.
  useEffect(() => {
    if (!value) setSelected(null);
    else if (!selected || selected.value !== value) setSelected({ value, label: initialLabel ?? value });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, initialLabel]);

  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
  }, []);

  const search = (q: string) => {
    if (timer.current) clearTimeout(timer.current);
    if (!q || (selected && selected.label === q)) return;
    timer.current = setTimeout(() => {
      setLoading(true);
      adminApi
        .get(endpoint, { params: { q } })
        .then((r) => setOptions(toOptions(r.data)))
        .catch(() => setOptions([]))
        .finally(() => setLoading(false));
    }, 250);
  };

  return (
    <Autocomplete
      size="small"
      options={selected && !options.some((o) => o.value === selected.value) ? [selected, ...options] : options}
      value={selected}
      loading={loading}
      filterOptions={(x) => x}
      isOptionEqualToValue={(a, b) => a.value === b.value}
      getOptionLabel={(o) => o.label}
      onInputChange={(_e, q, reason) => {
        if (reason === 'input') search(q);
      }}
      onChange={(_e, option) => {
        setSelected(option);
        onChange(option?.value ?? '', option);
      }}
      noOptionsText={t('report.filter.typeToSearch')}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={t('report.filter.typeToSearch')}
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress size={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              )
            }
          }}
        />
      )}
    />
  );
}

function StaticSelect({ label, endpoint, value, onChange }: { label: string; endpoint: string; value: string; onChange: (v: string) => void }) {
  const [options, setOptions] = useState<Option[]>([]);
  useEffect(() => {
    let alive = true;
    void loadStatic(endpoint).then((o) => alive && setOptions(o));
    return () => {
      alive = false;
    };
  }, [endpoint]);
  return (
    <FormControl size="small" fullWidth>
      <InputLabel>{label}</InputLabel>
      <Select label={label} value={options.some((o) => o.value === value) ? value : ''} onChange={(e) => onChange(String(e.target.value))}>
        <MenuItem value="">
          <em>{t('common.all')}</em>
        </MenuItem>
        {options.map((o) => (
          <MenuItem key={o.value} value={o.value}>
            {o.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

function DateRangeField({
  label,
  start,
  end,
  onChange
}: {
  label: string;
  start: string;
  end: string;
  onChange: (start: string, end: string) => void;
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const apply = (key: DatePresetKey) => {
    const [s, e] = presetRange(key);
    onChange(toInputValue(s), toInputValue(e));
    setAnchor(null);
  };
  return (
    <Stack spacing={0.5}>
      <FormLabel sx={{ fontSize: 12 }}>{label}</FormLabel>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
        <TextField
          type="datetime-local"
          size="small"
          label={t('report.startDate')}
          value={start}
          onChange={(e) => onChange(e.target.value, end)}
          slotProps={{ inputLabel: { shrink: true } }}
          fullWidth
        />
        <TextField
          type="datetime-local"
          size="small"
          label={t('report.endDate')}
          value={end}
          onChange={(e) => onChange(start, e.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
          fullWidth
        />
        <Button size="small" variant="outlined" startIcon={<Calendar size={16} />} onClick={(e) => setAnchor(e.currentTarget)} sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
          {t('report.filter.presets')}
        </Button>
        <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
          {datePresets.map((key) => (
            <MenuItem key={key} onClick={() => apply(key)}>
              <ListItemText>{t(`report.preset.${key}`)}</ListItemText>
            </MenuItem>
          ))}
        </Menu>
      </Stack>
    </Stack>
  );
}

function TriState({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <FormControl>
      <FormLabel sx={{ fontSize: 12 }}>{label}</FormLabel>
      <RadioGroup row value={value ?? ''} onChange={(e) => onChange(e.target.value)}>
        <FormControlLabel value="" control={<Radio size="small" />} label={t('common.all')} />
        <FormControlLabel value="1" control={<Radio size="small" />} label={t('status.yes')} />
        <FormControlLabel value="0" control={<Radio size="small" />} label={t('status.no')} />
      </RadioGroup>
    </FormControl>
  );
}

export type ReportFilterFormProps = {
  fields: FilterFieldName[];
  values: FilterValues;
  defaults: FilterValues;
  statusOptions?: string[];
  typeOptions?: string[];
  /** Labels for pre-filled remote selects (e.g. user name for ?user_id=). */
  initialLabels?: Record<string, string>;
  onSubmit: (values: FilterValues) => void;
};

const NO_OPTIONS: string[] = [];
const NO_LABELS: Record<string, string> = {};

export function ReportFilterForm({ fields, values, defaults, statusOptions = NO_OPTIONS, typeOptions = NO_OPTIONS, initialLabels = NO_LABELS, onSubmit }: ReportFilterFormProps) {
  const [form, setForm] = useState<FilterValues>(values);
  const [labels, setLabels] = useState<Record<string, string>>(initialLabels);
  useEffect(() => setForm(values), [values]);
  useEffect(() => setLabels((current) => ({ ...initialLabels, ...current })), [initialLabels]);

  const set = (name: string, value: string) => setForm((f) => ({ ...f, [name]: value }));
  const has = (name: FilterFieldName) => fields.includes(name);

  const rangeFields = useMemo(() => RANGE_FIELDS.filter(has), [fields]); // eslint-disable-line react-hooks/exhaustive-deps
  const triFields = useMemo(() => TRI_STATE_FIELDS.filter(has), [fields]); // eslint-disable-line react-hooks/exhaustive-deps

  const gridItems: React.ReactNode[] = [];
  for (const name of fields) {
    const remote = REMOTE_FIELDS[name];
    const stat = STATIC_FIELDS[name];
    if (remote) {
      gridItems.push(
        <RemoteSelect
          key={name}
          label={t(remote.label)}
          endpoint={remote.endpoint}
          value={form[name] ?? ''}
          initialLabel={labels[name]}
          onChange={(v, option) => {
            set(name, v);
            setLabels((l) => ({ ...l, [name]: option?.label ?? '' }));
          }}
        />
      );
    } else if (stat) {
      gridItems.push(<StaticSelect key={name} label={t(stat.label)} endpoint={stat.endpoint} value={form[name] ?? ''} onChange={(v) => set(name, v)} />);
    } else if ((name === 'status' && statusOptions.length) || (name === 'type' && typeOptions.length)) {
      const opts = name === 'status' ? statusOptions : typeOptions;
      gridItems.push(
        <FormControl key={name} size="small" fullWidth>
          <InputLabel>{t(`report.filter.${name}`)}</InputLabel>
          <Select label={t(`report.filter.${name}`)} value={form[name] ?? ''} onChange={(e) => set(name, String(e.target.value))}>
            <MenuItem value="">
              <em>{t('common.all')}</em>
            </MenuItem>
            {opts.map((o) => (
              <MenuItem key={o} value={o}>
                {valueLabel(o)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    } else if (TEXT_FIELDS.includes(name)) {
      gridItems.push(
        <TextField key={name} size="small" label={t(`report.filter.${name}`)} value={form[name] ?? ''} onChange={(e) => set(name, e.target.value)} fullWidth />
      );
    }
  }

  return (
    <Box
      component="form"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(form);
      }}
    >
      <Stack spacing={2}>
        {rangeFields.length ? (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: rangeFields.length > 1 ? '1fr 1fr' : '1fr' }, gap: 2 }}>
            {rangeFields.map((name) => (
              <DateRangeField
                key={name}
                label={t(name === 'date' ? 'report.filter.date' : 'report.filter.createdAt')}
                start={form[`${name}_start`] ?? ''}
                end={form[`${name}_end`] ?? ''}
                onChange={(s, e) => setForm((f) => ({ ...f, [`${name}_start`]: s, [`${name}_end`]: e }))}
              />
            ))}
          </Box>
        ) : null}
        {gridItems.length ? (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2 }}>{gridItems}</Box>
        ) : null}
        {triFields.length ? (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 1 }}>
            {triFields.map((name) => (
              <TriState key={name} label={t(`report.filter.${name}`)} value={form[name] ?? ''} onChange={(v) => set(name, v)} />
            ))}
          </Box>
        ) : null}
        <Stack direction="row" spacing={1}>
          <Button type="submit" variant="contained" size="small">
            {t('action.submit')}
          </Button>
          <Button
            size="small"
            onClick={() => {
              setForm(defaults);
              setLabels({});
              onSubmit(defaults);
            }}
          >
            {t('action.reset')}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}

/**
 * Serialize form values into the query params each legacy report controller reads.
 *  - primary range → start_date / end_date
 *  - items report's created_at range → start_created_at / end_created_at
 *  - from_location / to_location → from_location_id / to_location_id
 */
export function serializeFilters(
  fields: FilterFieldName[],
  values: FilterValues,
  options: { primaryRange: 'date' | 'created_at'; secondaryCreatedAt?: boolean },
  toBackend: (value: string, end?: boolean) => string
): Record<string, string> {
  const params: Record<string, string> = {};
  for (const name of fields) {
    if (name === 'date' || name === 'created_at') {
      const start = values[`${name}_start`];
      const end = values[`${name}_end`];
      const isPrimary = name === options.primaryRange;
      if (isPrimary) {
        if (start) params.start_date = toBackend(start);
        if (end) params.end_date = toBackend(end, true);
      } else if (name === 'created_at' && options.secondaryCreatedAt) {
        if (start) params.start_created_at = toBackend(start);
        if (end) params.end_created_at = toBackend(end, true);
      }
      continue;
    }
    const value = values[name];
    if (value == null || value === '') continue;
    if (name === 'from_location') params.from_location_id = value;
    else if (name === 'to_location') params.to_location_id = value;
    else params[name] = value;
  }
  return params;
}
